import type { GalleryFormat, GalleryItem } from "../config/campaign";

const ASPECT: Record<GalleryFormat, string> = {
  story: "9 / 16",
  portrait: "4 / 5",
  feed: "1 / 1",
  wide: "16 / 9",
};

const LABEL: Record<GalleryFormat, string> = {
  story: "Story",
  portrait: "Retrato",
  feed: "Feed",
  wide: "Horizontal",
};

const MIME: Record<string, string> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
  ".mp4": "video/mp4",
};

function extensionOf(file: string) {
  return (file.match(/\.[a-z0-9]+$/i)?.[0] || ".png").toLowerCase();
}

/**
 * Proporção do card: a medida do arquivo quando ela existe, a canônica do formato como
 * reserva. `format` serve para o rótulo e para a leitura rápida da grade — não para
 * enquadrar a arte, que costuma vir em proporção de impressão e não de tela.
 */
export function aspectFor(item: Pick<GalleryItem, "format" | "width" | "height">) {
  const { width, height } = item;
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return `${width} / ${height}`;
  }
  return ASPECT[item.format] || ASPECT.feed;
}

export function formatLabel(format: GalleryFormat) {
  return LABEL[format] || "Arte";
}

/** A grade desenha a miniatura; sem ela, o arquivo cheio — que pode ter alguns MB. */
export function thumbSource(item: GalleryItem) {
  return item.thumb || item.file;
}

/**
 * O nome com que a arte chega à galeria do celular. Sem o slug e o id, um segundo
 * download vira "download (1)" e a pessoa não sabe qual arte é qual.
 */
export function fileNameFor(slug: string, item: GalleryItem) {
  return `arte-${slug}-${item.id}${extensionOf(item.file)}`;
}

/** Só o `fetch` sabe o tipo real; isto é a reserva para quando o servidor não o declara. */
export function mimeFor(item: GalleryItem) {
  return MIME[extensionOf(item.file)] || "application/octet-stream";
}

export function shareTextFor(item: GalleryItem, fallback: string) {
  const own = item.shareText?.trim();
  return own || fallback;
}

/** O tipo vem da extensão, não de um campo à parte: campo repetido é campo que diverge. */
export function isVideo(item: Pick<GalleryItem, "file">) {
  return extensionOf(item.file) === ".mp4";
}

/**
 * Duração no selo do card. Vídeo de campanha passa de 15 MB, e quem está com dados
 * móveis merece saber que aquilo não é um card antes de tocar.
 */
export function formatDuration(seconds?: number) {
  if (!Number.isFinite(seconds) || (seconds as number) <= 0) return "";
  const total = Math.round(seconds as number);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
